/**
 * VH-114: pure helpers shared by the capture UI — they turn a load payload
 * into the editable state that ExerciseCaptureBlock renders.
 *
 * These live outside ExerciseCaptureBlock.jsx so that module exports only its
 * component — react-refresh/only-export-components (stricter as of
 * eslint-plugin-react-refresh 0.5) warns when a file mixes component and
 * non-component exports, because it breaks Vite fast refresh.
 */

// Empty string / nullish -> null; otherwise parse to a finite number.
export function toNumberOrNull(value) {
  if (value === '' || value === null || value === undefined) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

// Coerce a target/actual (which may arrive as a string decimal like "80.00")
// into a value suitable for a controlled input: '' when absent, else a number.
export function toInputValue(value) {
  if (value === '' || value === null || value === undefined) return ''
  const n = Number(value)
  return Number.isFinite(n) ? n : ''
}

// VH-278: a target as it should be read on a phone. "102.50" arrives as a decimal
// string; "102.5" says the same in less of a 390 px row. Anything that is not a
// number is shown as it came.
export function displayNumber(value) {
  if (value === '' || value === null || value === undefined) return null
  const n = Number(value)
  return Number.isFinite(n) ? `${n}` : `${value}`
}

/**
 * Decide whether a load block is set-based. Prefers the explicit `per_set`
 * flag embedded by the backend; falls back to "has a sets array".
 */
export function isBlockPerSet(block) {
  if (block && typeof block.per_set === 'boolean') return block.per_set
  return Array.isArray(block?.sets)
}

/**
 * Turn a load block's planned sets into editable rows, pre-filling with the
 * actual if captured, else the target ("leave unchanged = I hit the target").
 */
export function buildSetRows(sets) {
  return (sets || []).map((s) => ({
    set_number: s.set_number,
    set_type: s.set_type,
    target_reps_min: s.target_reps_min,
    target_reps_max: s.target_reps_max,
    target_weight: s.target_weight,
    target_reps: s.target_reps,
    // Seed from the PRIOR SESSION on both axes, so an untouched row reproduces
    // a set the user actually performed and "leave it alone to record a match"
    // means what it says.
    //
    // VH-159: reps used to seed from `target_reps_min` — the program's
    // prescribed rep FLOOR, identical on every set — while weight seeded from
    // history. Reported from production: a best bench session of five sets at
    // 85 kg for 5, 5, 5, 6, 7 reps rendered as five sets of 85 kg x 8, a
    // session that never happened. Never mix a prescription with a measurement.
    actual_reps: toInputValue(s.actual_reps ?? s.target_reps),
    actual_weight: toInputValue(s.actual_weight ?? s.target_weight),
    weight_notation: s.weight_notation ?? '',
  }))
}

/**
 * Turn a metric-based load block into an editable `{key: value}` map,
 * pre-filling with the saved actual if present, else the "target to beat"
 * (best prior value) so leaving it unchanged records a match.
 */
export function buildMetricValues(block) {
  const values = {}
  const fields = block?.metric_fields || []
  const saved = block?.metric_values || {}
  const targets = block?.metric_targets || {}
  fields.forEach((f) => {
    values[f.key] = toInputValue(saved[f.key] ?? targets[f.key])
  })
  return values
}
